import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBox, faPlus, faEdit, faTrash, faLink, faUnlink, 
  faSearch, faFilter, faGavel, faCheckCircle, faTimesCircle, faTimes
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import ECRCodeSelector from '../forms/ECRCodeSelector';
import { useGlobalData } from '../../contexts/GlobalDataContext';

interface MasterArticle {
  _id: string;
  masterArticleNumber: string;
  name: string;
  description?: string;
  category?: string;
  subCategory?: string;
  unit?: string;
  contentSize?: string;
  isAuctionable: boolean;
  linkedSupplierArticles: Array<{
    supplierArticleId: string;
    supplierId: string;
    creditorNumber: string;
    externalArticleNumber: string;
    supplierName: string;
    isActive: boolean;
  }>;
  authorizedSuppliers?: Array<{
    supplierId: string;
    supplierName: string;
    kreditorNumber?: string;
    isActive: boolean;
    authorizedAt: string;
    authorizedBy?: string;
  }>;
  isActive: boolean;
  createdAt: string;
}

const MasterArticleManagement: React.FC = () => {
  const [masterArticles, setMasterArticles] = useState<MasterArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [auctionableFilter, setAuctionableFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArticleSelectionModal, setShowArticleSelectionModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<MasterArticle | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);

  useEffect(() => {
    loadMasterArticles();
  }, [pagination.page, searchTerm, categoryFilter, auctionableFilter]);

  // Auto-close toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadMasterArticles = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category = categoryFilter;
      if (auctionableFilter) params.isAuctionable = auctionableFilter;

      const response = await api.get('/admin/master-articles', { params });
      setMasterArticles(response.data.masterArticles);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading master articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Master-Artikel wirklich deaktivieren?')) return;

    try {
      await api.delete(`/admin/master-articles/${id}`);
      loadMasterArticles();
    } catch (error) {
      console.error('Error deleting master article:', error);
      alert('Fehler beim Löschen des Master-Artikels');
    }
  };

  const handleAutoCreate = async () => {
    if (!window.confirm('Master-Artikel automatisch aus CreditorNumber erstellen? Dies kann einige Minuten dauern.')) {
      return;
    }

    try {
      const response = await api.post('/admin/master-articles/auto-create-from-creditor', {
        dryRun: false,
        category: 'Obst & Gemüse' // Optional: nur für O&G
      });

      alert(`Erfolgreich erstellt: ${response.data.results.created}\nÜbersprungen: ${response.data.results.skipped}`);
      loadMasterArticles();
    } catch (error: any) {
      console.error('Error auto-creating master articles:', error);
      alert('Fehler beim automatischen Erstellen: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="w-full px-6 py-4">
      {/* Header with Buttons */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faBox} className="text-orange-500" />
            Master-Artikel Management
          </h1>
          <p className="text-gray-600 text-sm">
            Verwalten Sie Master-Artikel und deren Lieferanten-Verknüpfungen
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAutoCreate}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm shadow-md hover:shadow-lg transition-all"
          >
            <FontAwesomeIcon icon={faLink} />
            Auto-Erstellen
          </button>
          <button
            onClick={() => setShowArticleSelectionModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm shadow-md hover:shadow-lg transition-all"
          >
            <FontAwesomeIcon icon={faBox} />
            Aus Artikeln erstellen
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm shadow-md hover:shadow-lg transition-all"
          >
            <FontAwesomeIcon icon={faPlus} />
            Neuer Master-Artikel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
        <div className="flex gap-3 items-center">
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-2 whitespace-nowrap">
              <FontAwesomeIcon icon={faSearch} />
              Suche
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Artikelnummer oder Name..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
              <FontAwesomeIcon icon={faFilter} className="mr-1" />
              Kategorie
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Alle Kategorien</option>
              <option value="Obst & Gemüse">Obst & Gemüse</option>
              <option value="Fleisch">Fleisch</option>
              <option value="Milchprodukte">Milchprodukte</option>
              <option value="Trockenwaren">Trockenwaren</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
              <FontAwesomeIcon icon={faGavel} className="mr-1" />
              Auktionierbar
            </label>
            <select
              value={auctionableFilter}
              onChange={(e) => setAuctionableFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Alle</option>
              <option value="true">Ja</option>
              <option value="false">Nein</option>
            </select>
          </div>

          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
              setAuctionableFilter('');
            }}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors whitespace-nowrap"
          >
            Filter zurücksetzen
          </button>
        </div>
      </div>

      {/* Master Articles List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Master-Artikel...</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Artikelnummer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Kategorie
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Lieferanten
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Auktionierbar
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {masterArticles.map((article) => (
                  <tr key={article._id} className="hover:bg-orange-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faBox} className="text-orange-500" />
                        <span className="font-semibold text-gray-900 text-sm">{article.masterArticleNumber}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{article.name}</div>
                        {article.description && (
                          <div className="text-xs text-gray-500 truncate max-w-md">{article.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {article.category || 'Keine'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-900">
                        {(article.linkedSupplierArticles.filter((l: any) => l.isActive).length + 
                          (article.authorizedSuppliers?.filter((a: any) => a.isActive).length || 0))}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">Lieferant(en)</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {article.isAuctionable ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-lg" />
                      ) : (
                        <FontAwesomeIcon icon={faTimesCircle} className="text-gray-300 text-lg" />
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => setSelectedArticle(article)}
                        className="text-orange-600 hover:text-orange-900 mr-3 transition-colors"
                        title="Bearbeiten"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-lg" />
                      </button>
                      <button
                        onClick={() => handleDelete(article._id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Löschen"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Zurück
              </button>
              <span className="px-4 py-2">
                Seite {pagination.page} von {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Weiter
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || selectedArticle) && (
        <MasterArticleModal
          article={selectedArticle}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedArticle(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setSelectedArticle(null);
            loadMasterArticles();
          }}
          setToast={setToast}
        />
      )}

      {/* Article Selection Modal */}
      {showArticleSelectionModal && (
        <ArticleSelectionModal
          onClose={() => setShowArticleSelectionModal(false)}
          onSave={() => {
            setShowArticleSelectionModal(false);
            loadMasterArticles();
          }}
          setToast={setToast}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 transition-all ${ 
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faTimesCircle} />
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-75"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
    </div>
  );
};

// Modal Component (simplified - you can expand this)
const MasterArticleModal: React.FC<{
  article: MasterArticle | null;
  onClose: () => void;
  onSave: () => void;
  setToast: (toast: {message: string, type: 'success' | 'error'} | null) => void;
}> = ({ article, onClose, onSave, setToast }) => {
  const [formData, setFormData] = useState({
    masterArticleNumber: article?.masterArticleNumber || '',
    name: article?.name || '',
    description: article?.description || '',
    category: article?.category || '',
    unit: article?.unit || '',
    contentSize: article?.contentSize || '',
    isAuctionable: article?.isAuctionable || false
  });
  
  const [linkedArticles, setLinkedArticles] = useState<any[]>([]);
  const [authorizedSuppliers, setAuthorizedSuppliers] = useState<any[]>([]);
  const [showAddArticleModal, setShowAddArticleModal] = useState(false);
  const [availableArticles, setAvailableArticles] = useState<any[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [allSuppliers, setAllSuppliers] = useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Load linked articles when modal opens
  useEffect(() => {
    if (article?._id) {
      loadLinkedArticles();
    }
  }, [article?._id]);

  const loadLinkedArticles = async () => {
    if (!article) return;
    try {
      const response = await api.get(`/admin/master-articles/${article._id}`);
      console.log('Full master article response:', response.data);
      console.log('Loaded linked articles:', response.data.linkedSupplierArticles);
      console.log('Loaded authorized suppliers:', response.data.authorizedSuppliers);
      setLinkedArticles(response.data.linkedSupplierArticles || []);
      setAuthorizedSuppliers(response.data.authorizedSuppliers || []);
    } catch (error) {
      console.error('Error loading linked articles:', error);
      setLinkedArticles([]);
    }
  };

  const loadAllSuppliers = async () => {
    if (!article) return;
    
    try {
      setLoadingSuppliers(true);
      // Lade alle Lieferanten ohne Pagination-Limit
      const response = await api.get('/suppliers?limit=10000&isActive=true');
      setAllSuppliers(response.data.suppliers || response.data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAllSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleAddSupplier = async (supplier: any) => {
    if (!article) return;
    
    try {
      setLoadingAvailable(true);
      console.log('Adding supplier:', supplier.name, 'to article:', article._id);
      
      // Lade verfügbare Artikel des Lieferanten
      const response = await api.get(`/admin/master-articles/${article._id}/available-supplier-articles?supplierId=${supplier._id}`);
      const articles = Array.isArray(response.data) ? response.data : (response.data.articles || []);
      
      console.log('Available articles from supplier:', articles);
      
      if (articles.length === 0) {
        setToast({ message: `Keine verfügbaren Artikel von ${supplier.name} gefunden.`, type: 'error' });
        setLoadingAvailable(false);
        return;
      }
      
      // Nimm den ersten verfügbaren Artikel
      const firstArticle = articles[0];
      console.log('Linking article:', firstArticle.articleNumber, firstArticle.name);
      
      const linkResponse = await api.post(`/admin/master-articles/${article._id}/link-supplier-article`, {
        supplierArticleId: firstArticle._id
      });
      
      console.log('Link response:', linkResponse.data);
      
      // Reload linked articles
      await loadLinkedArticles();
      
      // Success message
      setToast({ message: `Lieferant ${supplier.name} erfolgreich hinzugefügt!`, type: 'success' });
      
      setShowAddArticleModal(false);
      setSupplierSearchTerm('');
      setSelectedSupplier(null);
    } catch (error: any) {
      console.error('Error adding supplier:', error);
      setToast({ message: 'Fehler: ' + (error.response?.data?.message || error.message), type: 'error' });
    } finally {
      setLoadingAvailable(false);
    }
  };

  const loadAvailableArticles = async (supplierId: string) => {
    if (!article) return;
    
    try {
      setLoadingAvailable(true);
      const response = await api.get(`/admin/master-articles/${article._id}/available-supplier-articles?supplierId=${supplierId}`);
      // Handle both array and object responses
      const articles = Array.isArray(response.data) ? response.data : (response.data.articles || []);
      setAvailableArticles(articles);
    } catch (error) {
      console.error('Error loading available articles:', error);
      setAvailableArticles([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleAddArticle = async (supplierArticleId: string) => {
    if (!article) return;
    
    try {
      await api.post(`/admin/master-articles/${article._id}/link-supplier-article`, {
        supplierArticleId
      });
      
      // Reload linked articles
      const response = await api.get(`/admin/master-articles/${article._id}`);
      setLinkedArticles(response.data.linkedSupplierArticles);
      
      // Reload available articles if supplier is selected
      if (selectedSupplier) {
        loadAvailableArticles(selectedSupplier._id);
      }
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveArticle = async (supplierArticleId: string) => {
    if (!article) return;
    
    if (!window.confirm('Verknüpfung wirklich entfernen?')) return;
    
    try {
      await api.delete(`/admin/master-articles/${article._id}/unlink-supplier-article/${supplierArticleId}`);
      
      // Reload linked articles
      await loadLinkedArticles();
      
      // Reload available articles if supplier is selected
      if (selectedSupplier) {
        loadAvailableArticles(selectedSupplier._id);
      }
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveAuthorizedSupplier = async (supplierId: string) => {
    if (!article) return;
    
    if (!window.confirm('Autorisierung wirklich entfernen?')) return;
    
    try {
      await api.delete(`/admin/master-articles/${article._id}/unauthorize-supplier/${supplierId}`);
      
      // Reload authorized suppliers
      await loadLinkedArticles();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (article) {
        await api.put(`/admin/master-articles/${article._id}`, formData);
      } else {
        await api.post('/admin/master-articles', formData);
      }
      onSave();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {article ? 'Master-Artikel bearbeiten' : 'Neuer Master-Artikel'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Artikelnummer *
            </label>
            <input
              type="text"
              value={formData.masterArticleNumber}
              onChange={(e) => setFormData({ ...formData, masterArticleNumber: e.target.value })}
              required
              disabled={!!article}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorie
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Keine</option>
                <option value="Obst & Gemüse">Obst & Gemüse</option>
                <option value="Fleisch">Fleisch</option>
                <option value="Milchprodukte">Milchprodukte</option>
                <option value="Trockenwaren">Trockenwaren</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Einheit
              </label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Einheit wählen...</option>
                <option value="KG">KG</option>
                <option value="Stück">Stück</option>
                <option value="Karton">Karton</option>
                <option value="Palette">Palette</option>
                <option value="Liter">Liter</option>
                <option value="Packung">Packung</option>
                <option value="Box">Box</option>
              </select>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isAuctionable}
              onChange={(e) => setFormData({ ...formData, isAuctionable: e.target.checked })}
              className="mr-2"
            />
            <label className="text-sm font-medium text-gray-700">
              Für Auktionen freigeben
            </label>
          </div>

          {/* Linked Supplier Articles Section */}
          {article && (
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Verknüpfte Lieferanten</h3>
                <button
                  type="button"
                  onClick={() => {
                    loadAllSuppliers();
                    setShowAddArticleModal(true);
                  }}
                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  Lieferant hinzufügen
                </button>
              </div>

              {linkedArticles.length === 0 && authorizedSuppliers.length === 0 ? (
                <p className="text-gray-500 text-sm">Keine Lieferanten verknüpft</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lieferant</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kreditor</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {linkedArticles.map((item: any) => (
                        <tr key={`article-${item._id}`} className="bg-green-50">
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-100">
                              Artikel
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">{item.supplier?.name || '-'}</td>
                          <td className="px-3 py-2">{item.supplier?.kreditorNumber || '-'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveArticle(item._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Verknüpfung entfernen"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {authorizedSuppliers.filter((auth: any) => auth.isActive).map((auth: any) => (
                        <tr key={`auth-${auth.supplierId}`} className="bg-blue-50">
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-blue-700 bg-blue-100">
                              Autorisiert
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">{auth.supplierName || '-'}</td>
                          <td className="px-3 py-2">{auth.kreditorNumber || '-'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveAuthorizedSupplier(auth.supplierId)}
                              className="text-red-600 hover:text-red-800"
                              title="Autorisierung entfernen"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            >
              Speichern
            </button>
          </div>
        </form>

        {/* Add Article Modal */}
        {showAddArticleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {selectedSupplier ? `Artikel von ${selectedSupplier.name} auswählen` : 'Lieferant auswählen'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddArticleModal(false);
                    setSupplierSearchTerm('');
                    setSelectedSupplier(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {!selectedSupplier ? (
                // Step 1: Select Supplier
                <>
                  {/* Search Bar */}
                  <div className="mb-4">
                    <div className="relative">
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        value={supplierSearchTerm}
                        onChange={(e) => setSupplierSearchTerm(e.target.value)}
                        placeholder="Suche nach Lieferant oder Kreditor..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      {supplierSearchTerm && (
                        <button
                          onClick={() => setSupplierSearchTerm('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                  </div>

                  {loadingSuppliers ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-500">Lade Lieferanten...</div>
                    </div>
                  ) : (() => {
                    const filteredSuppliers = allSuppliers.filter(supplier => {
                      if (!supplierSearchTerm) return true;
                      const searchLower = supplierSearchTerm.toLowerCase();
                      return (
                        supplier.name?.toLowerCase().includes(searchLower) ||
                        supplier.kreditorNumber?.toLowerCase().includes(searchLower) ||
                        supplier.email?.toLowerCase().includes(searchLower)
                      );
                    });

                    return filteredSuppliers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {supplierSearchTerm 
                          ? `Keine Lieferanten gefunden für "${supplierSearchTerm}"`
                          : 'Keine Lieferanten gefunden'}
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Lieferant
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Kreditor
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Aktion
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredSuppliers.map((supplier) => (
                              <tr key={supplier._id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-900">
                                  {supplier.name}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {supplier.kreditorNumber || '-'}
                                </td>
                                <td className="px-4 py-3">
                                  {supplier.email || '-'}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <button
                                    onClick={() => handleAddSupplier(supplier)}
                                    className="inline-flex items-center px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-colors"
                                    disabled={loadingAvailable}
                                  >
                                    {loadingAvailable ? 'Wird hinzugefügt...' : (
                                      <>
                                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                        Lieferant hinzufügen
                                      </>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {/* Results counter */}
                        <div className="bg-gray-50 px-4 py-2 border-t text-sm text-gray-600">
                          {filteredSuppliers.length} {filteredSuppliers.length === 1 ? 'Lieferant' : 'Lieferanten'} gefunden
                          {supplierSearchTerm && ` für "${supplierSearchTerm}"`}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                // Step 2: Select Article from chosen Supplier
                <>
                  <button
                    onClick={() => setSelectedSupplier(null)}
                    className="mb-4 text-orange-500 hover:text-orange-600 text-sm"
                  >
                    ← Zurück zur Lieferantenauswahl
                  </button>

                  {loadingAvailable ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-500">Lade Artikel von {selectedSupplier.name}...</div>
                    </div>
                  ) : availableArticles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Keine verfügbaren Artikel von {selectedSupplier.name} gefunden
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto border rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Artikel-Nr
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Aktion
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {availableArticles.map((item: any) => (
                            <tr key={item._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                                {item.articleNumber}
                              </td>
                              <td className="px-4 py-3">
                                {item.name}
                              </td>
                              <td className="px-4 py-3 text-right whitespace-nowrap">
                                <button
                                  onClick={() => {
                                    handleAddArticle(item._id);
                                    setShowAddArticleModal(false);
                                    setSupplierSearchTerm('');
                                    setSelectedSupplier(null);
                                  }}
                                  className="inline-flex items-center px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                  Hinzufügen
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      <div className="bg-gray-50 px-4 py-2 border-t text-sm text-gray-600">
                        {availableArticles.length} {availableArticles.length === 1 ? 'Artikel' : 'Artikel'} 
                        {' '}von {selectedSupplier.name}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Article Selection Modal for Bulk Creation
interface SupplierArticleForSelection {
  _id: string;
  articleNumber: string;
  name: string;
  category?: string;
  gpcCategory?: string;
  ecrCode?: string;
  creditorNumber?: string;
  supplierId: {
    _id: string;
    name: string;
  };
  gtin?: string;
  contentSize?: string;
  unit?: string;
}

// Helper function to find ECR code by fullCode
const findEcrCodeByFullCode = (codes: any[], fullCode: string): any => {
  for (const code of codes) {
    if (code.fullCode === fullCode) return code;
    if (code.children && code.children.length > 0) {
      const found = findEcrCodeByFullCode(code.children, fullCode);
      if (found) return found;
    }
  }
  return null;
};

const ArticleSelectionModal: React.FC<{
  onClose: () => void;
  onSave: () => void;
  setToast: (toast: { type: 'success' | 'error'; message: string } | null) => void;
}> = ({ onClose, onSave, setToast }) => {
  const { ecrCodes } = useGlobalData();
  const [articles, setArticles] = useState<SupplierArticleForSelection[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [gpcCategoryFilter, setGpcCategoryFilter] = useState('');
  const [selectedEcrCodes, setSelectedEcrCodes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [gpcCategories, setGpcCategories] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState({ created: 0, linked: 0, skipped: 0, pendingSuppliers: 0, authorizedSuppliers: 0 });
  const [showGroupingModal, setShowGroupingModal] = useState(false);
  const [masterArticleNumber, setMasterArticleNumber] = useState('');
  const [masterArticleName, setMasterArticleName] = useState('');
  const [masterArticleCategory, setMasterArticleCategory] = useState('');
  const [masterArticleUnit, setMasterArticleUnit] = useState('');
  const [isAuctionable, setIsAuctionable] = useState(false);
  const [showSupplierSelectionModal, setShowSupplierSelectionModal] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [additionalSupplierIds, setAdditionalSupplierIds] = useState<Set<string>>(new Set());

  const loadAllSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await api.get('/suppliers?limit=10000&isActive=true');
      setAllSuppliers(response.data.suppliers || response.data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAllSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleAddSupplierToSelection = async (supplier: any) => {
    try {
      // Füge nur die Lieferanten-ID hinzu, nicht die Artikel
      setAdditionalSupplierIds(prev => new Set(Array.from(prev).concat(supplier._id)));
      
      // Modal bleibt offen, so dass weitere Lieferanten hinzugefügt werden können
      setToast({ type: 'success', message: `Lieferant ${supplier.name} hinzugefügt` });
    } catch (error: any) {
      console.error('Error adding supplier:', error);
      setToast({ type: 'error', message: 'Fehler: ' + (error.response?.data?.message || error.message) });
    }
  };

  useEffect(() => {
    loadArticles();
  }, [pagination.page, searchTerm, categoryFilter, selectedEcrCodes]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        status: 'all' // Lade alle Status
      };

      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category = categoryFilter;
      
      // Filter nach ausgewählten ECR-Codes - backend erwartet gpcCategory als komma-separierte Liste
      if (selectedEcrCodes.length > 0) {
        params.ecrCodes = selectedEcrCodes.join(',');
      }

      const response = await api.get('/articles', { params });
      
      // Response von /api/articles ist { articles, total, currentPage, totalPages }
      setArticles(response.data.articles || []);
      
      // Extrahiere verfügbare Kategorien aus den Artikeln
      const allCategories = Array.from(new Set(
        (response.data.articles || [])
          .map((a: any) => a.category)
          .filter((c: string) => c && c.trim())
      )) as string[];
      
      const allGpcCategories = Array.from(new Set(
        (response.data.articles || [])
          .map((a: any) => a.gpcCategory)
          .filter((c: string) => c && c.trim())
      )) as string[];
      
      setCategories(allCategories.sort());
      setGpcCategories(allGpcCategories.sort());
      
      setPagination({
        page: response.data.currentPage || 1,
        limit: pagination.limit,
        total: response.data.total || 0,
        pages: response.data.totalPages || 1
      });
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleArticle = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleAll = () => {
    if (selectedIds.size === articles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(articles.map(a => a._id)));
    }
  };

  const handleSubmit = async () => {
    // Pre-fill form with first selected article's data
    const selectedArticles = articles.filter(a => selectedIds.has(a._id));
    if (selectedArticles.length > 0) {
      const firstArticle = selectedArticles[0];
      setMasterArticleNumber(firstArticle.articleNumber || '');
      setMasterArticleName(firstArticle.name || '');
      setMasterArticleCategory(firstArticle.ecrCode || firstArticle.gpcCategory || '');
      setMasterArticleUnit(firstArticle.unit || '');
    }
    
    setShowConfirmModal(false);
    setShowGroupingModal(true);
  };

  const handleCreateMasterArticle = async () => {
    if (!masterArticleNumber || !masterArticleName) {
      alert('Bitte Artikelnummer und Name eingeben');
      return;
    }

    try {
      setLoading(true);
      
      // Erstelle zuerst den Master-Artikel
      const masterArticleData = {
        masterArticleNumber,
        name: masterArticleName,
        category: masterArticleCategory || 'Sonstiges',
        unit: masterArticleUnit,
        isAuctionable
      };

      const masterResponse = await api.post('/admin/master-articles', masterArticleData);
      const masterArticleId = masterResponse.data.masterArticle?._id || masterResponse.data._id;
      
      console.log('Created master article:', masterResponse.data);
      console.log('Master article ID:', masterArticleId);

      // Verknüpfe alle ausgewählten Supplier-Articles
      let linked = 0;
      const selectedArticles = articles.filter(a => selectedIds.has(a._id));
      
      for (const article of selectedArticles) {
        try {
          console.log(`Linking article ${article._id} to master ${masterArticleId}`);
          await api.post(`/admin/master-articles/${masterArticleId}/link-supplier-article`, {
            supplierArticleId: article._id
          });
          linked++;
        } catch (error) {
          console.error('Error linking article:', article._id, error);
        }
      }

      // Autorisiere zusätzliche Lieferanten
      let authorized = 0;
      for (const supplierId of Array.from(additionalSupplierIds)) {
        try {
          console.log(`Authorizing supplier ${supplierId} for master ${masterArticleId}`);
          await api.post(`/admin/master-articles/${masterArticleId}/authorize-supplier`, {
            supplierId
          });
          authorized++;
        } catch (error) {
          console.error('Error authorizing supplier:', supplierId, error);
        }
      }

      setResultMessage({
        created: 1,
        linked: linked,
        skipped: selectedArticles.length - linked,
        pendingSuppliers: 0,
        authorizedSuppliers: authorized
      });
      setShowGroupingModal(false);
      setShowResultModal(true);
      setSelectedIds(new Set());
      setAdditionalSupplierIds(new Set());
      
      // Reset form
      setMasterArticleNumber('');
      setMasterArticleName('');
      setMasterArticleCategory('');
      setMasterArticleUnit('');
      setIsAuctionable(false);
    } catch (error: any) {
      console.error('Error creating master article:', error);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faBox} className="text-green-500" />
              Master-Artikel erstellen
            </h2>
            <p className="text-gray-600 mt-1">
              Wählen Sie einen Artikel aus und fügen Sie Lieferanten hinzu, die auktionieren dürfen
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
            title="Schließen"
          >
            <FontAwesomeIcon icon={faTimes} className="text-2xl" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Suche
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Artikelnummer oder Name..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Kategorie
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
              >
                <option value="">Alle Kategorien</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                ECR/GPC Warengruppe (Mehrfachauswahl)
              </label>
              <div className="space-y-2">
                <ECRCodeSelector
                  codes={ecrCodes}
                  selectedCode=""
                  onSelect={(code) => {
                    if (!selectedEcrCodes.includes(code.fullCode)) {
                      setSelectedEcrCodes([...selectedEcrCodes, code.fullCode]);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }
                  }}
                  placeholder="ECR Warengruppe hinzufügen..."
                  className="w-full"
                />
                {selectedEcrCodes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedEcrCodes.map(code => {
                      const ecrCode = findEcrCodeByFullCode(ecrCodes, code);
                      return (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                        >
                          {ecrCode?.description || code}
                          <button
                            onClick={() => {
                              setSelectedEcrCodes(selectedEcrCodes.filter(c => c !== code));
                              setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                            className="hover:text-green-900"
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex justify-between items-center">
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('');
                setSelectedEcrCodes([]);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Filter zurücksetzen
            </button>
            <div className="text-sm text-gray-600">
              {selectedIds.size > 0 ? '1 Artikel ausgewählt' : 'Kein Artikel ausgewählt'}
            </div>
          </div>
        </div>

        {/* Article List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Lade Artikel...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Keine Artikel gefunden</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left w-10">
                      Auswahl
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
                      Artikelnummer
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">
                      Kategorie
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-64">
                      ECR/GPC Warengruppe
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">
                      Lieferant
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
                      Kreditor
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {articles.map((article) => (
                    <tr 
                      key={article._id} 
                      className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has(article._id) ? 'bg-green-50' : ''}`}
                      onClick={() => toggleArticle(article._id)}
                    >
                      <td className="px-3 py-2 w-10">
                        <input
                          type="radio"
                          checked={selectedIds.has(article._id)}
                          onChange={() => {
                            // Nur einen Artikel auswählen (Radio-Button Logik)
                            setSelectedIds(new Set([article._id]));
                          }}
                          className="rounded-full"
                        />
                      </td>
                      <td className="px-3 py-2 w-32">
                        <span className="text-xs font-medium text-gray-900">{article.articleNumber}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-gray-900">{article.name}</div>
                      </td>
                      <td className="px-3 py-2 w-40">
                        <span className="text-xs text-gray-600">{article.category || '-'}</span>
                      </td>
                      <td className="px-3 py-2 w-64">
                        <div className="text-xs text-gray-600 line-clamp-2" title={article.gpcCategory || '-'}>
                          {article.gpcCategory || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 w-48">
                        <div className="text-xs text-gray-600 truncate" title={article.supplierId?.name || '-'}>
                          {article.supplierId?.name || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 w-24">
                        <span className="text-xs text-gray-600">{article.creditorNumber || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t bg-gray-50 flex justify-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 bg-white border rounded-lg disabled:opacity-50 text-sm"
            >
              Zurück
            </button>
            <span className="px-3 py-1 text-sm">
              Seite {pagination.page} von {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 bg-white border rounded-lg disabled:opacity-50 text-sm"
            >
              Weiter
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <strong>{selectedIds.size}</strong> Artikel ausgewählt
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              onClick={() => {
                if (selectedIds.size === 0) return;
                handleSubmit();
              }}
              disabled={loading || selectedIds.size === 0}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50 text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              {loading ? 'Erstelle...' : 'Weiter'}
            </button>
          </div>
        </div>

        {/* Confirm Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Master-Artikel erstellen?
              </h3>
              <p className="text-gray-600 mb-6">
                Sie haben <strong>{selectedIds.size} Artikel</strong> ausgewählt. Diese werden zu einem gemeinsamen Master-Artikel zusammengefasst.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm"
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grouping Modal */}
        {showGroupingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Master-Artikel Details
                  </h3>
                  <p className="text-gray-600 mt-1">
                    Definieren Sie den Master-Artikel für die {selectedIds.size} ausgewählten Lieferanten-Artikel
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowGroupingModal(false);
                    setMasterArticleNumber('');
                    setMasterArticleName('');
                    setMasterArticleCategory('');
                    setMasterArticleUnit('');
                    setIsAuctionable(false);
                  }}
                  className="ml-4 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
                  title="Schließen"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Master Article Form */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Master-Artikelnummer *
                    </label>
                    <input
                      type="text"
                      value={masterArticleNumber}
                      onChange={(e) => setMasterArticleNumber(e.target.value)}
                      placeholder="z.B. MA-001"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={masterArticleName}
                      onChange={(e) => setMasterArticleName(e.target.value)}
                      placeholder="Master-Artikel Name"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Einheit *
                    </label>
                    <select
                      value={masterArticleUnit}
                      onChange={(e) => setMasterArticleUnit(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Einheit wählen...</option>
                      <option value="KG">KG</option>
                      <option value="Stück">Stück</option>
                      <option value="Karton">Karton</option>
                      <option value="Palette">Palette</option>
                      <option value="Liter">Liter</option>
                      <option value="Packung">Packung</option>
                      <option value="Box">Box</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ECR Warengruppe
                    </label>
                    <ECRCodeSelector
                      codes={ecrCodes}
                      selectedCode={masterArticleCategory}
                      onSelect={(code) => setMasterArticleCategory(code.fullCode)}
                      placeholder="ECR-Code auswählen..."
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAuctionable}
                        onChange={(e) => setIsAuctionable(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        <FontAwesomeIcon icon={faGavel} className="mr-1" />
                        Für Auktionen freigeben
                      </span>
                    </label>
                  </div>
                </div>

                {/* Selected Articles Preview */}
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      Ausgewählter Artikel und Lieferanten (1 + {additionalSupplierIds.size})
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        loadAllSuppliers();
                        setShowSupplierSelectionModal(true);
                      }}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      Weitere Lieferanten hinzufügen
                    </button>
                  </div>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Typ</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Artikelnr.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Lieferant</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Kreditor</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {articles.filter(a => selectedIds.has(a._id)).map(article => (
                          <tr key={article._id} className="bg-green-50">
                            <td className="px-3 py-2 text-xs font-medium">
                              <span className="inline-flex items-center px-2 py-1 rounded text-green-700 bg-green-100">
                                Master-Artikel
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs">{article.articleNumber}</td>
                            <td className="px-3 py-2 text-xs">{article.name}</td>
                            <td className="px-3 py-2 text-xs">{article.supplierId?.name || '-'}</td>
                            <td className="px-3 py-2 text-xs">{article.creditorNumber || '-'}</td>
                          </tr>
                        ))}
                        {Array.from(additionalSupplierIds).map(supplierId => {
                          const supplier = allSuppliers.find(s => s._id === supplierId);
                          return supplier ? (
                            <tr key={`supplier-${supplierId}`} className="bg-blue-50">
                              <td className="px-3 py-2 text-xs">
                                <span className="inline-flex items-center px-2 py-1 rounded text-blue-700 bg-blue-100">
                                  Berechtigt
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs italic" colSpan={2}>
                                Später verknüpfen
                              </td>
                              <td className="px-3 py-2 text-xs font-medium">{supplier.name}</td>
                              <td className="px-3 py-2 text-xs">{supplier.kreditorNumber || '-'}</td>
                            </tr>
                          ) : null;
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowGroupingModal(false);
                    setMasterArticleNumber('');
                    setMasterArticleName('');
                    setMasterArticleCategory('');
                    setMasterArticleUnit('');
                    setIsAuctionable(false);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
                  disabled={loading}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreateMasterArticle}
                  disabled={loading || !masterArticleNumber || !masterArticleName || !masterArticleUnit}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50 text-sm"
                >
                  {loading ? 'Erstelle...' : 'Master-Artikel erstellen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Supplier Selection Modal within Grouping */}
        {showSupplierSelectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
              <div className="bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-lg flex-shrink-0">
                <h2 className="text-xl font-semibold">Lieferant hinzufügen</h2>
                <button
                  onClick={() => setShowSupplierSelectionModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  title="Schließen"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>

              <div className="p-6 flex-shrink-0">
                {/* Search bar */}
                <div>
                  <input
                    type="text"
                    placeholder="Suche nach Name, Kreditor oder E-Mail..."
                    value={supplierSearchTerm}
                    onChange={(e) => setSupplierSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Suppliers table - scrollable area */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {loadingSuppliers ? (
                  <div className="text-center py-8">
                    <i className="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
                    <p className="text-gray-500 mt-2">Lade Lieferanten...</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Lieferant
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kreditor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            E-Mail
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Aktion
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allSuppliers
                          .filter((supplier: any) => {
                            const searchLower = supplierSearchTerm.toLowerCase();
                            return (
                              supplier.name?.toLowerCase().includes(searchLower) ||
                              supplier.kreditorNumber?.toLowerCase().includes(searchLower) ||
                              supplier.email?.toLowerCase().includes(searchLower)
                            );
                          })
                          .map((supplier: any) => {
                            const isAlreadyAdded = additionalSupplierIds.has(supplier._id);
                            return (
                              <tr key={supplier._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {supplier.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {supplier.kreditorNumber || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {supplier.email || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  {isAlreadyAdded ? (
                                    <span className="text-green-600">
                                      <i className="fas fa-check mr-1"></i>
                                      Hinzugefügt
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => handleAddSupplierToSelection(supplier)}
                                      className="text-green-600 hover:text-green-900"
                                      title="Lieferant hinzufügen"
                                    >
                                      <i className="fas fa-plus mr-1"></i>
                                      Hinzufügen
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    {allSuppliers.filter((supplier: any) => {
                      const searchLower = supplierSearchTerm.toLowerCase();
                      return (
                        supplier.name?.toLowerCase().includes(searchLower) ||
                        supplier.kreditorNumber?.toLowerCase().includes(searchLower) ||
                        supplier.email?.toLowerCase().includes(searchLower)
                      );
                    }).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        Keine Lieferanten gefunden
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Result Modal */}
        {showResultModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-3xl" />
                <h3 className="text-lg font-bold text-gray-900">
                  Erfolgreich erstellt!
                </h3>
              </div>
              <div className="space-y-2 mb-6">
                <p className="text-gray-700">
                  <strong className="text-green-600">1</strong> Master-Artikel erfolgreich erstellt
                </p>
                {resultMessage.linked > 0 && (
                  <p className="text-green-700">
                    <strong className="text-green-600">{resultMessage.linked}</strong> Artikel verknüpft
                  </p>
                )}
                {resultMessage.authorizedSuppliers > 0 && (
                  <p className="text-green-700">
                    <strong className="text-green-600">{resultMessage.authorizedSuppliers}</strong> Lieferanten autorisiert
                  </p>
                )}
                <p className="text-gray-600 text-sm mt-2">
                  <strong>{resultMessage.linked + resultMessage.authorizedSuppliers}</strong> Lieferanten insgesamt können jetzt auf diesen Artikel bieten!
                </p>
                {resultMessage.skipped > 0 && (
                  <p className="text-orange-600">
                    <strong>{resultMessage.skipped}</strong> Artikel konnten nicht verknüpft werden
                  </p>
                )}
                {resultMessage.pendingSuppliers > 0 && (
                  <p className="text-blue-600">
                    <strong>{resultMessage.pendingSuppliers}</strong> Lieferanten konnten nicht autorisiert werden
                  </p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    onSave();
                  }}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterArticleManagement;
